import { useCallback, useEffect, useState } from "react";
import { useMutation } from "react-query";

import { params } from "@/params";
import { useApi } from "@/api/useApi";
import { useAuthorizedMutation, useAuthorizedQuery } from "@/api";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import { KnowledgeHubConfig } from "../parts/core/pages/knowledge-hub/index.types";

export const useGetConfiguration = () => {
  return useAuthorizedQuery({
    queryKey: "knowledge-hub-configuration",
    url: `${params.API_URL}/platform/knowledge-hub/configuration`,
    method: "GET",
    enabled: true,
  });
};

export const useUpdateConfiguration = () => {
  return useAuthorizedMutation({
    url: `${params.API_URL}/platform/knowledge-hub/configuration`,
    method: "PUT",
  });
};

export type UploadKnowledgeHubAssestResponse = {
  name: string;
  path: string;
};

export function useUploadKnowledgeHubAssest() {
  const { api } = useApi();

  return useMutation<UploadKnowledgeHubAssestResponse, unknown, File>(
    async (file) => {
      const formData = new FormData();
      formData.append("file", file);

      const res = await api.post(
        `${params.API_URL}/platform/knowledge-hub/upload-assest`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return res.data;
    }
  );
}

export function useKnowledgeHubConfiguration() {
  const { data, isLoading, refetch } = useGetConfiguration();

  const updateConfigurationMutation = useUpdateConfiguration();

  const [updateStatus, setUpdateStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [localConfiguration, setLocalConfiguration] =
    useState<KnowledgeHubConfig | null>(null);

  const updateConfiguration = useCallback(
    async (configuration: KnowledgeHubConfig) => {
      setUpdateStatus("saving");
      const res = await updateConfigurationMutation.mutateAsync({
        configuration,
      });
      await refetch();

      setUpdateStatus("saved");
      return res;
    },
    [updateConfigurationMutation.mutateAsync, refetch]
  );

  const debouncedUpdateConfiguration = useDebouncedCallback(
    updateConfiguration,
    1000
  );

  const handleUpdateConfiguration = useCallback(
    (configuration: KnowledgeHubConfig) => {
      setLocalConfiguration(configuration);
      debouncedUpdateConfiguration(configuration);
    },
    [debouncedUpdateConfiguration]
  );

  useEffect(() => {
    if (data != null) {
      setLocalConfiguration(data.knowledgeHubConfiguration);
    }
  }, [data]);

  useEffect(() => {
    if (updateStatus === "saved") {
      const timer = setTimeout(() => setUpdateStatus("idle"), 2000);
      return () => clearTimeout(timer);
    }
  }, [updateStatus]);

  return {
    configuration: localConfiguration,
    isLoading,
    updateConfiguration: handleUpdateConfiguration,
    updateStatus,
    token: data?.token,
  };
}
